import { useState } from 'react';
import { Dialog } from '@headlessui/react';

interface Task2FeedbackPopupProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (feedback: {
    q1: string;
    q2: string;
    q2Explanation: string;
    q3: string;
    q3Explanation: string;
  }) => void;
  taskTitle: string;
  error?: string;
  isSubmitting?: boolean;
}

const Task2FeedbackPopup = ({
  isOpen,
  onClose,
  onSubmit,
  taskTitle,
  error,
  isSubmitting = false,
}: Task2FeedbackPopupProps) => {
  const [q1, setQ1] = useState('');
  const [q2, setQ2] = useState('');
  const [q2Explanation, setQ2Explanation] = useState('');
  const [q3, setQ3] = useState('');
  const [q3Explanation, setQ3Explanation] = useState('');
  const [validationError, setValidationError] = useState('');

  const handleSubmit = () => {
    // Basic validation
    if (!q1 || !q2 || !q3) {
      setValidationError(
        'Please answer all required questions before submitting.'
      );
      return;
    }

    setValidationError('');
    onSubmit({ q1, q2, q2Explanation, q3, q3Explanation });
  };

  if (!isOpen) return null;

  const q1ScaleOptions = [
    { value: 'Not clear at all', label: 'Not clear at all' },
    { value: 'Slightly clear', label: 'Slightly clear' },
    { value: 'Moderately clear', label: 'Moderately clear' },
    { value: 'Mostly clear', label: 'Mostly clear' },
    { value: 'Very clear', label: 'Very clear' },
  ];

  const q2ChoiceOptions = [
    { value: 'Yes', label: 'Yes' },
    { value: 'Somewhat', label: 'Somewhat' },
    { value: 'No', label: 'No' },
  ];

  const q3MoodOptions = [
    { value: 'Confident', label: 'Confident' },
    { value: 'Clear', label: 'Clear' },
    { value: 'Neutral', label: 'Neutral' },
    { value: 'Confused', label: 'Confused' },
    { value: 'Frustrated', label: 'Frustrated' },
  ];

  return (
    <Dialog open={isOpen} onClose={onClose} className='relative z-50'>
      <div className='fixed inset-0 bg-black/30' aria-hidden='true' />
      <div className='fixed inset-0 flex items-center justify-center p-4'>
        <Dialog.Panel className='mx-auto w-[50vw] rounded bg-white p-6 shadow-xl'>
          <Dialog.Title className='text-lg font-bold mb-4'>
            Feedback for: {taskTitle}
          </Dialog.Title>

          {(error || validationError) && (
            <div
              className='bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4'
              role='alert'>
              <p>{error || validationError}</p>
            </div>
          )}

          <fieldset className='mb-4'>
            <legend className='mb-1 font-medium'>
              Question 1: How clear were the instructions in this task?{' '}
              <span className='text-red-600'>*</span>
            </legend>
            <div className='flex flex-col md:flex-row gap-2 md:gap-4 mt-2'>
              {q1ScaleOptions.map((option) => (
                <label
                  key={option.value}
                  className='flex items-center space-x-2 cursor-pointer'>
                  <input
                    type='radio'
                    name='q1_scale'
                    value={option.value}
                    checked={q1 === option.value}
                    onChange={(e) => setQ1(e.target.value)}
                    className='form-radio h-4 w-4 text-blue-600'
                  />
                  <span>{option.label}</span>
                </label>
              ))}
            </div>
          </fieldset>

          <fieldset className='mb-4'>
            <legend className='mb-1 font-medium'>
              Question 2: Did this task help you apply what you've learned so
              far? <span className='text-red-600'>*</span>
            </legend>
            <div className='flex flex-col md:flex-row gap-2 md:gap-4  mt-2'>
              {q2ChoiceOptions.map((option) => (
                <label
                  key={option.value}
                  className='flex items-center space-x-2 cursor-pointer'>
                  <input
                    type='radio'
                    name='q2_choice'
                    value={option.value}
                    checked={q2 === option.value}
                    onChange={(e) => setQ2(e.target.value)}
                    className='form-radio h-4 w-4 text-blue-600'
                  />
                  <span>{option.label}</span>
                </label>
              ))}
            </div>
            <label
              htmlFor='q2_explanation'
              className='block text-sm font-medium text-gray-700 mt-3 mb-1'>
              Tell us why you chose that answer (if you'd like to share):
            </label>
            <textarea
              id='q2_explanation'
              value={q2Explanation}
              onChange={(e) => setQ2Explanation(e.target.value)}
              className='w-full border rounded p-2'
              placeholder='Your thoughts...'
              rows={3}
            />
          </fieldset>

          <fieldset className='mb-4'>
            <legend className='mb-1 font-medium'>
              Question 3: How did this task make you feel?{' '}
              <span className='text-red-600'>*</span>
            </legend>
            <div className='flex flex-col md:flex-row gap-2 md:gap-4 mt-2'>
              {q3MoodOptions.map((option) => (
                <label
                  key={option.value}
                  className='flex items-center space-x-2 cursor-pointer'>
                  <input
                    type='radio'
                    name='q3_mood'
                    value={option.value}
                    checked={q3 === option.value}
                    onChange={(e) => setQ3(e.target.value)}
                    className='form-radio h-4 w-4 text-blue-600'
                  />
                  <span>{option.label}</span>
                </label>
              ))}
            </div>
            <label
              htmlFor='q3_explanation'
              className='block text-sm font-medium text-gray-700 mt-3 mb-1'>
              Would you like to tell us why you felt this way? (Optional)
            </label>
            <textarea
              id='q3_explanation'
              value={q3Explanation}
              onChange={(e) => setQ3Explanation(e.target.value)}
              className='w-full border rounded p-2'
              placeholder='Your thoughts...'
              rows={3}
            />
          </fieldset>

          <div className='flex justify-end space-x-2'>
            <button
              onClick={onClose}
              className='px-4 py-2 text-gray-700 bg-gray-200 rounded hover:bg-gray-300'>
              Cancel
            </button>
            <button
              onClick={handleSubmit}
              disabled={isSubmitting}
              className={`px-4 py-2 text-white rounded ${
                isSubmitting
                  ? 'bg-blue-400 cursor-not-allowed'
                  : 'bg-blue-600 hover:bg-blue-700'
              }`}>
              {isSubmitting ? 'Submitting...' : 'Submit Feedback'}
            </button>
          </div>
        </Dialog.Panel>
      </div>
    </Dialog>
  );
};

export default Task2FeedbackPopup;
